// Fires the Upgate checkout for the paywall popup. Authorizes via the
// x-auth-token header (not a cookie) so it works cross-origin, where a prelander
// can't set a cookie for the API's domain. baseUrl returns the user to /lobby.
import { useState } from 'react';
import { applyPriceMultiplier, resolvePriceMultiplier } from '../../utils/paywall';
import { markCheckoutStarted } from '../../utils/session';
import { isSfwVariation } from '../../utils/variation';

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`;
const API_KEY = process.env.REACT_APP_API_KEY;

const PLATFORM_BASE_URL =
  process.env.REACT_APP_PLATFORM_BASE_URL || SWIPEY_BASE_URL;

const MODAL_SOURCE = 'paywall_popup';

/**
 * Build the post-payment return URL the BE turns into Upgate's success_url.
 * `paywallType` ('paywall_popup') rides as a query param so the platform /lobby
 * can tag subscription_purchase with the experiment variant — uniformly for
 * google + password + magic-link popup buyers (google's OAuth `type` is the model
 * style, so it can't carry the popup marker any other way). Requires 'paywall_type'
 * in the backend ALLOWED_REDIRECT_PARAMS allowlist, else it's stripped before the
 * success_url is built.
 */
export const buildLobbyBaseUrl = (authToken, paywallType) =>
  `${PLATFORM_BASE_URL}/lobby?auth_token=${encodeURIComponent(authToken || '')}${
    paywallType ? `&paywall_type=${encodeURIComponent(paywallType)}` : ''
  }${
    // SFW arm (?variation=sfw) → the paid user lands in the protected experience.
    // Requires 'protection' in the backend ALLOWED_REDIRECT_PARAMS allowlist, else
    // it is stripped before the success_url is built.
    isSfwVariation() ? '&protection=sfw' : ''
  }`;

// The authToken behind a checkout is gone (spent by the platform, or past the
// backend's 1h Redis TTL) — the caller must re-authenticate rather than retry.
export const isAuthCheckoutError = (err) =>
  err?.status === 401 || err?.status === 403;

// Checkout texts shown on the Upgate hosted page, mirroring the platform's
// CheckoutModal subscription branch (frontend CheckoutModal/context.tsx):
// productName → title, description → subtitle, renewalDescription → the
// fine print the backend injects via payment_form_override CSS.
// The popup is English-only, so the strings are built in English here.
const buildCheckoutTexts = (
  plan,
  { bonusTokens, currencySymbol, isFlatRebill, priceMultiplier },
) => {
  const durationLabel = `${plan.duration_months} ${
    plan.duration_months > 1 ? 'months' : 'month'
  } subscription`;

  const productName = plan.discount_percent
    ? `${durationLabel} (${plan.discount_percent}% Off)`
    : durationLabel;

  const description = bonusTokens > 0 ? `Bonus Hearts: +${bonusTokens}` : '';

  // Renews at the full (undiscounted) price unless the user is in the flat-rebill
  // arm, which keeps charging the same amount — same rule as the platform.
  //
  // Multiplied up by the geo price multiplier: the plan the LP fetched is
  // unmultiplied (GET /subscription-plans only applies it for an authenticated
  // user), while the backend bills the multiplied amount — so the renewal figure
  // has to be scaled here to match what is actually charged.
  const baseRenewAmount = isFlatRebill
    ? plan.paymentAmount
    : plan.duration_months * plan.monthly_price;
  const renewAmount = applyPriceMultiplier(
    baseRenewAmount,
    resolvePriceMultiplier(priceMultiplier),
  );

  let renewalDescription = '';
  if (Number.isFinite(renewAmount) && renewAmount > 0) {
    const todayPart = isFlatRebill
      ? ''
      : `${currencySymbol}${plan.paymentAmount.toFixed(2)} today • `;
    const afterLabel = `${plan.duration_months} ${
      plan.duration_months > 1 ? 'Months' : 'Month'
    }`;
    renewalDescription = `${todayPart}Premium Renews at ${currencySymbol}${renewAmount.toFixed(2)} after ${afterLabel}. Cancel anytime.`;
  }

  return { productName, description, renewalDescription };
};

// Pure checkout call — no React state, so callers outside the popup (e.g. the
// popup-first flow in createAccount, which checks out with a FRESH authToken right
// after registration) can invoke it without a stale-closure hook instance.
// Returns the API response; navigates to the Upgate hosted page on success.
//   authToken: registration/login key, sent as x-auth-token + embedded in baseUrl.
//   bonusTokens: the heart-pill value for the plan (keeps the checkout text in
//   sync with what the popup displayed); currencySymbol: geo symbol from
//   useCurrentCurrency; locale: lander i18n language for the checkout page.
export async function startUpgateCheckout({
  authToken,
  plan,
  redirect = true,
  bonusTokens = 0,
  currencySymbol = '$',
  locale,
  paywallType,
  isFlatRebill,
  priceMultiplier,
} = {}) {
  if (!plan?._id) throw new Error('checkout: plan._id is required');
  // x-auth-token: undefined would POST an unauthenticated request that surfaces
  // as a generic 401 — fail loudly instead so the caller can recover.
  if (!authToken) throw new Error('checkout: authToken is required');

  const { productName, description, renewalDescription } = buildCheckoutTexts(
    plan,
    {
      bonusTokens,
      currencySymbol,
      isFlatRebill: isFlatRebill ?? plan.isFlatRebill ?? false,
      priceMultiplier,
    },
  );

  const body = {
    baseUrl: buildLobbyBaseUrl(authToken, paywallType),
    productName,
    description,
    renewalDescription,
    ...(locale && { locale }),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    modal_source: MODAL_SOURCE,
    usePaywallInitialTokensBonus: true,
  };

  const res = await fetch(
    `${API_BASE_URL}/subscription-plans/${plan._id}/upgate-checkout-external`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        'x-auth-token': authToken,
      },
      body: JSON.stringify(body),
    },
  );
  const data = await res.json();
  if (!res.ok) {
    // Carry the status so callers can tell "this token is dead" (401/403 — spent
    // by the platform or aged out of Redis) from a transient failure the user can
    // simply retry. See isAuthCheckoutError.
    const error = new Error(data?.message || `HTTP error! status: ${res.status}`);
    error.status = res.status;
    throw error;
  }

  if (redirect && data?.redirectUrl) {
    // Leaving for the hosted checkout. Stamp the stored session so a return —
    // Back from Upgate, a cancel bounce, a bfcache restore — reopens the paywall
    // popup with this token instead of the signup form (see utils/session).
    markCheckoutStarted();
    window.location.href = data.redirectUrl;
  }
  return data;
}

// authToken: registration key, sent as x-auth-token + embedded in baseUrl.
export function useUpgateCheckout({ authToken } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // checkout(plan, opts) → { redirectUrl, ... }; navigates on success.
  const checkout = async (plan, opts = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      return await startUpgateCheckout({ ...opts, authToken, plan });
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { checkout, isLoading, error };
}
